import SummaryProductList from "components/OrderDetail/SummaryProducts/SummaryProductsList"
import OrderDetailHOC from "hoc/OrderDetailHOC"
import { OrderDetailType } from "types/OrderType"
import useTransitOrder from "overrides/theme/privilege/base/Pages/DetailOrderScreens/Transit/UseTransitOrder"
import TransitAction from "components/OrderDetail/TransitProductOrder/TransitAction"
import SkeletonShippinGroupDetail from "components/Commons/Skeleton/SkeletonShippinGroupDetail"
import TagPrintContent from "components/OrderDetail/TagPrint/TagPrintContent"
import { Suspense, useContext, useEffect, useReducer, useState } from "react"
import { AuthContext } from "context/context/AuthContext"
import { clients } from "webapp-clients/Clients"
import AlertStyled from "theme/common/Alert.styled"
import { IconAttachment, IconBackArow } from "theme/common/Icon.styled"
import { ErrorFieldForm } from "theme/common/Paragraph.styled"
import useHideActionButtons from "hooks/UseHideActionButtons"
import { IconCancel } from "theme/common/Icon.styled"
import { ButtonStyled } from "theme/common/Button.styled"
import ContentDetailOrderActionsStyled from "theme/common/ContentDetailOrderActions.styled"
import { EShippinggroupAction } from "enums/shippinggroupEmun"
import useCancelDecreaseOrder from "hooks/UseCancelDecreaseOrder"
import UseDeliveredRejectOrder from "hooks/UseDeliveredRejectOrder"
import UseCustomerRejectOrder from "hooks/UseCustomerRejectOrder"
import { WebAppClientsEnum } from "enums/CommonEnum"
import UseCancelOrder from "hooks/UseCancelOrder"
import { useGetCancelActionByClient } from "hooks/UseGetMultiClient"
import pickingReducer, { initialStatePicking } from "pages/DetailOrderScreens/Confirm/PickingReducer"
import ProductItemRow from "overrides/theme/privilege/base/Pages/DetailOrderScreens/Confirm/ProductItemRow"
import ProcessProductsList from "components/OrderDetail/ProcessProducts/ProcessProductsList"
import { IProcessItem } from "interfaces/IItemsOrder"
import { LinkIconStyled } from "theme/common/Link.styled"
import { useLocation } from "react-router-dom"

// TODO: multiclient
import useFormConfig from "hooks/Media/useFormConfig"
import useFormConfigSandbox from "overrides/theme/sandbox/base/Hooks/Media/useFormConfig"
import { useEnv } from "hooks"

const TransitScreen = ({
  shippinGroupDetail,
  shippingGroupId,
  setFinishedShippingGroupAction,
  finishedShippingGroupAction,
  order,
}: OrderDetailType) => {
  const {
    state: { user },
  } = useContext(AuthContext)
  const { hideTransitActions, updateVisibilityButtonActions } = useHideActionButtons()
  const [_client] = useEnv().multiclient.get_user_env()
  const { pathname } = useLocation()
  const isTransitToSource = pathname.includes("transit_to_source")

  // State for series input (only for transit_to_source)
  const [pickingTransition, dispachPickingTransition] = useReducer(pickingReducer, initialStatePicking)
  const [itemsAllProcessed, setItemsAllProcessed] = useState(false)

  useEffect(() => {
    updateVisibilityButtonActions(shippinGroupDetail?.source?.id, shippinGroupDetail?.target?.source?.id)
  }, [shippinGroupDetail, updateVisibilityButtonActions])

  const client = clients[_client as keyof typeof clients]
  const FilesList = client["FilesList" as keyof typeof client]
  const itemsOrder = shippinGroupDetail?.source.items
  const documents = shippinGroupDetail?.custom?.documents
  const elockerInfo = shippinGroupDetail?.custom?.infoElocker
  const NoExistTransit = client["NoExistTransit" as keyof typeof client]

  // TODO: multiclient
  const useFormConfigWrapper = user?.mainGroup.id === "sandbox" ? useFormConfigSandbox : useFormConfig
  const { errors, FileUpload, handleSubmit, register, setValue } = useFormConfigWrapper({})

  const { submitDeliveredOrder, loadingTransit } = useTransitOrder({
    shippingGroupId,
    setFinishedShippingGroupAction,
    order,
    pickingTransition,
    isTransitToSource,
  })

  // Check if all items are processed (only for transit_to_source)
  useEffect(() => {
    if (!isTransitToSource) return

    if (pickingTransition.isEditing) {
      setItemsAllProcessed(false)
      return
    }
    if (itemsOrder?.length === pickingTransition.processItems.length) {
      setItemsAllProcessed(true)
    }
  }, [pickingTransition.processItems, pickingTransition.isEditing, itemsOrder, isTransitToSource])

  const { handleClickCancelDecreaseOrder, loadingCancelDecrase, canIuseCancelDecraese } = useCancelDecreaseOrder({
    order,
    shippingGroupId,
  })

  const { handleClickDeliveredRejectOrder, loadingDeliveredReject, canIuseDeliveredReject } = UseDeliveredRejectOrder({
    order,
    shippingGroupId,
  })

  const { handleClickCustomerRejectOrder, loadingCustomerReject, canIuseCustomerReject } = UseCustomerRejectOrder({
    order,
    shippingGroupId,
    setFinishedShippingGroupAction,
  })

  const handleClickCancelDecrease = () => {
    handleClickCancelDecreaseOrder(EShippinggroupAction.cancel_decrease)
  }

  const handleClickDeliveredReject = () => {
    handleClickDeliveredRejectOrder(EShippinggroupAction.delivered_reject)
  }

  const handleClickCustomerReject = () => {
    handleClickCustomerRejectOrder(EShippinggroupAction.customer_reject)
  }

  const [permissionOrder, actionOrder] = useGetCancelActionByClient()

  const handleClickCancel = () => {
    handleClickCancelOrder(actionOrder)
  }

  const { handleClickCancelOrder, loadingCancel, canIuseCancel } = UseCancelOrder({
    order,
    shippingGroupId,
    permissionOrder,
  })

  // Handlers for series input (only for transit_to_source)
  const handleClickProcessProduct = (item: IProcessItem) => {
    if (!item) return

    if (pickingTransition.isEditing) {
      const nextItemIndex = itemsOrder?.findIndex(
        (item) => !pickingTransition.processItems.some(({ sku }) => sku === item.sku),
      )
      dispachPickingTransition({
        type: "EDIT_ITEM_SERIES",
        payload: {
          itemEdit: item,
          nextItemIndex: nextItemIndex || -1,
        },
      })
      return
    }
    dispachPickingTransition({ type: "ADD_PROCESS_ITEM", payload: item })
  }

  const handleClickBackToScan = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setItemsAllProcessed(false)
    dispachPickingTransition({ type: "RESET_PICKING" })
  }

  const handleClickEdit = (sku: string) => {
    dispachPickingTransition({ type: "CHANGE_ITEM_ACTIVE_FOR_EDIT", payload: sku })
  }

  return (
    <>
      {NoExistTransit && (
        <Suspense fallback={null}>
          <NoExistTransit
            shippingGroupId={shippingGroupId}
            courierDeliveryInfo={shippinGroupDetail?.custom?.courierDeliveryInfo}
            shippinGroupDetail={shippinGroupDetail}
          />
        </Suspense>
      )}
      {itemsOrder ? (
        <>
          {canIuseCancel && _client === WebAppClientsEnum.sandbox && (
            <ContentDetailOrderActionsStyled>
              <ButtonStyled
                variant="secondary"
                onClick={handleClickCancel}
                loadingButton={loadingCancel}
                disabled={loadingCancel}
              >
                <IconCancel />
                <span>Cancelar Orden</span>
              </ButtonStyled>
            </ContentDetailOrderActionsStyled>
          )}

          {/* Show series input for transit_to_source */}
          {isTransitToSource && !finishedShippingGroupAction ? (
            <>
              {itemsAllProcessed && (
                <LinkIconStyled to="#" className="padding-content" onClick={handleClickBackToScan}>
                  <IconBackArow />
                  <span>Regresar</span>
                </LinkIconStyled>
              )}
              <AlertStyled variant="default">
                <p>
                  Para continuar, necesita escanear <br />
                  todos los productos
                </p>
              </AlertStyled>
              {!itemsAllProcessed && itemsOrder && (
                <div className="product-list-container">
                  {itemsOrder.map((item, index) => (
                    <ProductItemRow
                      key={item.sku + index}
                      itemOrder={item}
                      onClicProcessProduct={handleClickProcessProduct}
                      allOrderItems={itemsOrder}
                      source={shippinGroupDetail?.source.id}
                      hideConfirmActions={hideTransitActions}
                      pickingTransition={pickingTransition}
                      dispachPickingTransition={dispachPickingTransition}
                    />
                  ))}
                </div>
              )}

              <ProcessProductsList processItems={pickingTransition.processItems} onClickEditItem={handleClickEdit} />
            </>
          ) : (
            <SummaryProductList
              itemsOrder={isTransitToSource ? pickingTransition.processItems : itemsOrder}
              groupPicking={false}
              titleMain={finishedShippingGroupAction ? "Productos en tránsito" : undefined}
            />
          )}

          {!finishedShippingGroupAction && (
            <>
              <form onSubmit={handleSubmit(submitDeliveredOrder)}>
                {FileUpload && (
                  <Suspense fallback={null}>
                    <section className="padding-content ">
                      <AlertStyled variant="info" className="m-top-button1">
                        <IconAttachment />
                        <p>Adjuntar máximo 3 archivos</p>
                      </AlertStyled>
                      <FileUpload register={register} setValue={setValue} />
                    </section>
                  </Suspense>
                )}

                {errors.filesShippinGroup && <ErrorFieldForm>{errors.filesShippinGroup.message}</ErrorFieldForm>}

                {canIuseCancelDecraese && (
                  <ContentDetailOrderActionsStyled>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickCancelDecrease}
                      loadingButton={loadingCancelDecrase}
                      disabled={loadingCancelDecrase}
                    >
                      <IconCancel />
                      <span>Cancelar Orden sin Modificar Inventario</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {canIuseDeliveredReject && (
                  <ContentDetailOrderActionsStyled>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickDeliveredReject}
                      loadingButton={loadingDeliveredReject}
                      disabled={loadingDeliveredReject}
                    >
                      <IconCancel />
                      <span>Cancelar orden por siniestro</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {canIuseCustomerReject && (
                  <ContentDetailOrderActionsStyled>
                    <ButtonStyled
                      variant="secondary"
                      onClick={handleClickCustomerReject}
                      loadingButton={loadingCustomerReject}
                      disabled={loadingCustomerReject}
                    >
                      <IconCancel />
                      <span>Rechazado por el cliente</span>
                    </ButtonStyled>
                  </ContentDetailOrderActionsStyled>
                )}

                {!hideTransitActions && (!isTransitToSource || itemsAllProcessed) && <TransitAction loadingTransit={loadingTransit} />}
              </form>
            </>
          )}
          {order && (
            <TagPrintContent
              codigoEntrega={(elockerInfo?.isElocker && elockerInfo?.codigoEntrega) || null}
              order={order}
              shippingGroupId={shippingGroupId}
            />
          )}
          {FilesList && (
            <Suspense fallback={null}>
              <FilesList documents={documents} channel={shippinGroupDetail?.channel} orderId={order?.id} />
            </Suspense>
          )}
        </>
      ) : (
        <SkeletonShippinGroupDetail />
      )}
    </>
  )
}

export default OrderDetailHOC(TransitScreen)
